import {
  HIDDEN,
  NAV_SEARCH,
  SEARCH_ONLY,
  type AdminRouteDefinition,
} from './admin-route-types';

export const SECONDARY_ADMIN_ROUTES = [
  {
    id: 'inventory',
    label: 'Inventory Levels',
    description: 'Review stock levels, replenishment alerts, and quantity transactions.',
    group: 'system-data',
    icon: 'Package',
    lifecycle: 'canonical',
    pathPattern: '/inventory',
    href: '/inventory',
    searchTerms: ['stock', 'materials', 'warehouse', 'products'],
    visibility: NAV_SEARCH,
    surface: 'destination',
    audience: 'admin',
  },
  // Money controls
  {
    id: 'customer-economics',
    label: 'Customer economics',
    description: 'Review governed customer and service economics in the Money hub.',
    group: 'money-controls',
    icon: 'BarChart3',
    lifecycle: 'canonical',
    pathPattern: '/money',
    href: '/money?tab=economics',
    searchTerms: ['margin', 'profitability', 'financials', 'economics'],
    visibility: NAV_SEARCH,
    surface: 'destination',
    audience: 'admin',
    activeWhen: {
      pathname: '/money',
      query: { tab: 'economics' },
    },
  },
  {
    id: 'recurring-billing',
    label: 'Recurring billing',
    description: 'Configure recurring billing rules and policy.',
    group: 'money-controls',
    icon: 'Receipt',
    lifecycle: 'canonical',
    pathPattern: '/admin/recurring-billing',
    href: '/admin/recurring-billing',
    searchTerms: ['subscriptions', 'billing rules', 'repeat invoices'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'dead-letter-reviews',
    label: 'Dead-letter review',
    description: 'Review retained queue failures and record evidence without replaying work.',
    group: 'system-data',
    icon: 'ShieldCheck',
    lifecycle: 'canonical',
    pathPattern: '/admin/dead-letter-reviews',
    href: '/admin/dead-letter-reviews',
    searchTerms: ['queue', 'dead letter', 'failed jobs', 'no replay'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'sales-reps',
    label: 'Sales reps',
    description: 'Manage the sales rep roster used for 5% commission attribution.',
    group: 'money-controls',
    icon: 'Users',
    lifecycle: 'canonical',
    pathPattern: '/admin/sales-reps',
    href: '/admin/sales-reps',
    searchTerms: ['sales', 'rep', 'salesperson', 'commission', 'roster'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'sales-commissions',
    label: 'Sales commissions',
    description: 'Commission ledger: contract awards, payment accruals, and refund clawbacks.',
    group: 'money-controls',
    icon: 'Receipt',
    lifecycle: 'canonical',
    pathPattern: '/admin/commissions',
    href: '/admin/commissions',
    searchTerms: ['commission', 'ledger', 'payout', 'clawback', 'sales'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'rate-suggestions',
    label: 'Rate calibration',
    description: 'Review and approve evidence-backed production-rate suggestions.',
    group: 'money-controls',
    icon: 'Sparkles',
    lifecycle: 'canonical',
    pathPattern: '/admin/rate-suggestions',
    href: '/admin/rate-suggestions',
    searchTerms: ['suggestions', 'calibration queue', 'approve rates'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'estimate-accuracy',
    label: 'Estimate accuracy',
    description: 'Compare estimated property effort with actual production evidence.',
    group: 'advanced',
    icon: 'Target',
    lifecycle: 'detail',
    pathPattern: '/admin/reports/estimate-accuracy',
    href: '/admin/reports/estimate-accuracy',
    searchTerms: ['estimates', 'actual hours', 'variance', 'report'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'crew-event-ledger',
    label: 'Crew event ledger',
    description: 'Browse the append-only record of crew actions.',
    group: 'advanced',
    icon: 'BookOpen',
    lifecycle: 'canonical',
    pathPattern: '/admin/ledger',
    href: '/admin/ledger',
    searchTerms: ['crew actions', 'event history', 'append only', 'evidence'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'customer-reconciliation-detail',
    label: 'Customer reconciliation',
    description: 'Reconcile Stripe and Mission Control records for a selected customer.',
    group: 'money-controls',
    icon: 'Receipt',
    lifecycle: 'detail',
    pathPattern: '/admin/customers/reconcile/[id]',
    searchTerms: ['stripe', 'billing mismatch', 'customer reconcile'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'customer-friction-compatibility',
    label: 'Customer friction',
    description: 'Legacy address for the Customer Hub friction view.',
    group: 'money-controls',
    icon: 'TrendingUp',
    lifecycle: 'compatibility',
    pathPattern: '/admin/customers/friction',
    href: '/admin/customers/friction',
    canonicalHref: '/customers-hub?tab=friction',
    searchTerms: ['complaints', 'churn', 'cancellations', 'customer hub'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'customer-losers-compatibility',
    label: 'Unprofitable customers',
    description: 'Retired address for customer economics in the Money hub.',
    group: 'money-controls',
    icon: 'BarChart3',
    lifecycle: 'compatibility',
    pathPattern: '/admin/customers/losers',
    href: '/admin/customers/losers',
    canonicalHref: '/money?tab=economics',
    searchTerms: ['losers', 'low margin', 'unprofitable customers'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'pricing-review-compatibility',
    label: 'Pricing review',
    description: 'Review proposed price changes in the canonical Money workspace.',
    group: 'money-controls',
    icon: 'DollarSign',
    lifecycle: 'compatibility',
    pathPattern: '/admin/pricing-review',
    href: '/admin/pricing-review',
    canonicalHref: '/money?tab=price-review',
    searchTerms: ['price changes', 'renewals', 'pricing recommendations'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'pricing-trust-compatibility',
    label: 'Pricing trust',
    description: 'Inspect pricing-trust evidence in the canonical Money workspace.',
    group: 'money-controls',
    icon: 'ShieldCheck',
    lifecycle: 'compatibility',
    pathPattern: '/admin/trust',
    href: '/admin/trust',
    canonicalHref: '/money?tab=trust',
    searchTerms: ['trust checks', 'pricing evidence', 'shadow authority'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'renewals-generate-compatibility',
    label: 'Generate renewals',
    description: 'Retired renewal address retained for compatibility with old links.',
    group: 'money-controls',
    icon: 'Receipt',
    lifecycle: 'compatibility',
    pathPattern: '/admin/renewals/generate',
    href: '/admin/renewals/generate',
    canonicalHref: '/money?tab=economics',
    searchTerms: ['renewal generation', 'price renewal'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'guarantee-returns',
    label: 'Guarantee returns by crew',
    description: 'Completed no-charge guarantee returns per crew and Central month, with recorded labor and cost evidence.',
    group: 'field-operations',
    icon: 'ClipboardList',
    lifecycle: 'canonical',
    pathPattern: '/admin/reports/guarantee-returns',
    href: '/admin/reports/guarantee-returns',
    searchTerms: ['guarantee', 'return visit', 're-service', 'redo', 'free visit', 'crew', 'monthly report'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'season-hours-retired',
    label: 'Season hours forecast',
    description: 'Legacy capacity forecast retained while Labor & Capacity carries the primary workflow.',
    group: 'money-controls',
    icon: 'Clock',
    lifecycle: 'retired',
    pathPattern: '/admin/reports/season-hours',
    href: '/admin/reports/season-hours',
    searchTerms: ['season forecast', 'contracted hours', 'labor report'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },

  // Communications & growth
  {
    id: 'communications',
    label: 'Delivery & review automation',
    description: 'Monitor outbound delivery and administer automated review requests.',
    group: 'communications-growth',
    icon: 'MessageSquare',
    lifecycle: 'canonical',
    pathPattern: '/admin/comms',
    href: '/admin/comms',
    searchTerms: ['communications', 'email', 'sms', 'alerts', 'google reviews'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'message-templates',
    label: 'Message templates',
    description: 'Browse the versioned email and SMS copy currently used by automations.',
    group: 'communications-growth',
    icon: 'Send',
    lifecycle: 'canonical',
    pathPattern: '/admin/comms/templates',
    href: '/admin/comms/templates',
    searchTerms: ['email templates', 'sms templates', 'copy', 'messages'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'voice-health',
    label: 'Voice agent health',
    description: 'Inspect phone and voice-automation delivery health.',
    group: 'communications-growth',
    icon: 'Radio',
    lifecycle: 'canonical',
    pathPattern: '/admin/voice-health',
    href: '/admin/voice-health',
    searchTerms: ['calls', 'phone', 'voice ai', 'telephony'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'annual-marketing',
    label: 'Annual marketing plan',
    description: 'Review annual plans, channel controls, and pending decisions.',
    group: 'communications-growth',
    icon: 'Megaphone',
    lifecycle: 'canonical',
    pathPattern: '/admin/marketing/annual',
    href: '/admin/marketing/annual',
    searchTerms: ['marketing plan', 'channels', 'annual plan'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'marketing-conversions',
    label: 'Conversion tracking',
    description: 'Configure marketing conversion sources and tracking.',
    group: 'communications-growth',
    icon: 'TrendingUp',
    lifecycle: 'canonical',
    pathPattern: '/admin/marketing/conversions',
    href: '/admin/marketing/conversions',
    searchTerms: ['conversion tracking', 'attribution', 'leads', 'marketing'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },

  // System & data
  {
    id: 'integrations',
    label: 'Integrations & AI',
    description: 'Connect external systems and configure autonomous agents.',
    group: 'system-data',
    icon: 'Zap',
    lifecycle: 'canonical',
    pathPattern: '/admin/integrations',
    href: '/admin/integrations',
    searchTerms: ['stripe', 'ghl', 'bouncie', 'openai', 'agents', 'connections'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'data-import',
    label: 'Data import',
    description: 'Import operational records and inspect import outcomes.',
    group: 'system-data',
    icon: 'FileSpreadsheet',
    lifecycle: 'canonical',
    pathPattern: '/admin/import',
    href: '/admin/import',
    searchTerms: ['csv', 'upload', 'migration', 'service log'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'system-health',
    label: 'System health',
    description: 'Monitor active operational failures and recent recovery.',
    group: 'system-data',
    icon: 'Activity',
    lifecycle: 'canonical',
    pathPattern: '/admin/health',
    href: '/admin/health',
    searchTerms: ['status', 'failures', 'heartbeat', 'operations', 'diagnostics'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'admin-overview',
    label: 'Settings overview',
    description: 'Find administrative controls by task, responsibility, or familiar language.',
    group: 'system-data',
    icon: 'Settings',
    lifecycle: 'canonical',
    pathPattern: '/admin',
    href: '/admin',
    searchTerms: ['settings', 'admin', 'administration', 'control tower', 'find a setting'],
    visibility: { navigation: false, commandPalette: true, suggested: true },
    surface: 'route',
    audience: 'admin',
    activeWhen: {
      pathname: '/admin',
      includeDescendants: false,
    },
  },
  {
    id: 'dev-tracker-retired',
    label: 'Development tracker',
    description: 'Read-only history from the retired in-app development tracker.',
    group: 'advanced',
    icon: 'MoreHorizontal',
    lifecycle: 'retired',
    pathPattern: '/admin/dev',
    href: '/admin/dev',
    searchTerms: ['dev work', 'backlog', 'fleet history'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'dev-tracker-detail',
    label: 'Development task history',
    description: 'Read-only detail from the retired development tracker.',
    group: 'advanced',
    icon: 'MoreHorizontal',
    lifecycle: 'retired',
    pathPattern: '/admin/dev/[id]',
    searchTerms: ['dev task', 'backlog history'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
] as const satisfies readonly AdminRouteDefinition[];
